import { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BookOpen, MapPin, Phone, Mail, Globe, ExternalLink, Building2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tenerife center
const TENERIFE_CENTER = [28.2916, -16.6291];

// Custom marker icon for libraries
const libraryIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background-color: #6366f1; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Map bounds controller
function MapController({ data }) {
  const map = useMap();
  
  useEffect(() => {
    if (data && data.length > 0) {
      const validItems = data.filter(item => item.lat && item.lon);
      if (validItems.length > 0) {
        const bounds = L.latLngBounds(validItems.map(item => [item.lat, item.lon]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [data, map]);
  
  return null;
}

export function LibrariesPage() {
  const { language } = useLanguage();
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchLibraries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/services/libraries`);
      setLibraries(response.data || []);
    } catch (error) {
      console.error('Error fetching libraries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLibraries();
  }, [fetchLibraries]);

  const validLibraries = libraries.filter(item => item.lat && item.lon);

  const openInGoogleMaps = (lat, lon, name) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold font-['Outfit']">
                {language === 'es' ? 'Bibliotecas' : 'Libraries'}
              </h1>
              <p className="text-indigo-100">
                {language === 'es' 
                  ? 'Bibliotecas y ludotecas en Tenerife'
                  : 'Libraries and toy libraries in Tenerife'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="mb-6 flex items-center gap-4">
          <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
            <span className="text-2xl font-bold text-indigo-600">{validLibraries.length}</span>
            <span className="text-gray-600 ml-2">
              {language === 'es' ? 'bibliotecas' : 'libraries'}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Map */}
            <div className="lg:col-span-2">
              <Card className="overflow-hidden">
                <div className="h-[500px]">
                  <MapContainer
                    center={TENERIFE_CENTER}
                    zoom={10}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapLocationButton language={language} />
                    <MapController data={validLibraries} />
                    <MarkerClusterGroup chunkedLoading>
                      {validLibraries.map((item, index) => (
                        <Marker
                          key={index}
                          position={[item.lat, item.lon]}
                          icon={libraryIcon}
                          eventHandlers={{
                            click: () => setSelectedItem(item),
                          }}
                        >
                          <Popup>
                            <div className="p-2 min-w-[200px]">
                              <h3 className="font-bold text-gray-900 mb-2">{item.nombre}</h3>
                              <p className="text-sm text-gray-600 mb-1">{item.tipo}</p>
                              {item.direccion && (
                                <p className="text-sm text-gray-600 mb-1">
                                  <MapPin className="w-3 h-3 inline mr-1" />
                                  {item.direccion}
                                </p>
                              )}
                              {item.municipio && (
                                <p className="text-sm text-gray-500 mb-2">{item.municipio}</p>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full mt-2"
                                onClick={() => openInGoogleMaps(item.lat, item.lon, item.nombre)}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                {language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps'}
                              </Button>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  </MapContainer>
                </div>
              </Card>
            </div>

            {/* List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {language === 'es' ? 'Listado' : 'List'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-[440px] overflow-y-auto">
                  <div className="space-y-3">
                    {validLibraries.slice(0, 50).map((item, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedItem === item
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedItem(item)}
                      >
                        <h4 className="font-medium text-gray-900 text-sm">{item.nombre}</h4>
                        <p className="text-xs text-gray-500 mt-1">{item.municipio}</p>
                        {item.telefono && (
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {item.telefono}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
